'use client';

import React, { useEffect, useState, useRef } from 'react';
import Link from 'next/link';

export default function AyatHero() {
  const [loaded, setLoaded] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (cursorRef.current) {
        cursorRef.current.style.left = `${e.clientX}px`;
        cursorRef.current.style.top = `${e.clientY}px`;
      }
    };
    section.addEventListener('mousemove', handleMouseMove);
    return () => section.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section ref={sectionRef} className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden hero-gradient-bg grain-overlay pt-20">
      <div
        ref={cursorRef}
        style={{ position: 'fixed', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,105,20,0.1) 0%, transparent 65%)', pointerEvents: 'none', transform: 'translate(-50%, -50%)', zIndex: 2, willChange: 'transform' }}
      />
      <div className="pixel-border-gold-top" />
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(139,105,20,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(139,105,20,0.04) 1px, transparent 1px)', backgroundSize: '60px 60px', zIndex: 0 }} />
      {/* Radial lines from center */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <svg viewBox="-300 -300 600 600" width="700" height="700" style={{ opacity: 0.22 }}>
          {Array.from({ length: 36 }, (_, i) => {
            const angle = (i / 36) * 360;
            const rad = (angle * Math.PI) / 180;
            const innerR = 60;
            const outerR = 280;
            return (
              <line key={i}
                x1={innerR * Math.cos(rad)} y1={innerR * Math.sin(rad)}
                x2={outerR * Math.cos(rad)} y2={outerR * Math.sin(rad)}
                stroke="rgba(139,105,20,0.5)" strokeWidth="0.5"
              />
            );
          })}
          {[60, 120, 180, 240]?.map((r) => (
            <circle key={r} cx="0" cy="0" r={r} fill="none" stroke="rgba(139,105,20,0.3)" strokeWidth="0.5" />
          ))}
          <circle cx="0" cy="0" r="55" fill="#1C2B25" stroke="rgba(179,138,42,0.5)" strokeWidth="1" />
          <text x="0" y="-6" textAnchor="middle" fill="#F5F0E8" fontSize="10" fontFamily="monospace" fontWeight="900">AYAT</text>
          <text x="0" y="10" textAnchor="middle" fill="rgba(179,138,42,0.7)" fontSize="6" fontFamily="monospace" opacity="1">IN CREATION</text>
        </svg>
      </div>
      <div className={`relative z-10 max-w-[900px] mx-auto px-6 text-center transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <p className="font-mono text-xs tracking-widest uppercase mb-6" style={{ color: 'rgba(139,105,20,0.7)' }}>03 — Open-World Journey</p>
        <h1 className="font-mono text-5xl md:text-7xl font-black uppercase tracking-tight leading-none mb-6" style={{ color: '#F5F0E8' }}>
          AYAT IN<br />
          <span className="text-shimmer">CREATION</span>
        </h1>
        <p className="font-mono text-sm max-w-lg mx-auto leading-relaxed mb-12" style={{ color: 'rgba(245,240,232,0.5)' }}>
          Signs in creation as vectors pointing back to Allah. Each thing perceived as a sign, a proof, an evidence — a ray from the origin pointing back to the One.
        </p>
        <div className="flex items-center justify-center gap-6">
          <Link href="#signs-map" className="font-mono text-xs tracking-widest uppercase px-6 py-3 transition-colors"
            style={{ background: 'rgba(139,105,20,0.9)', color: '#F5F0E8', border: '1px solid rgba(179,138,42,0.4)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(179,138,42,0.95)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(139,105,20,0.9)'; }}
          >
            Explore Signs
          </Link>
          <Link href="/bridge-irl-application" className="font-mono text-xs tracking-widest uppercase transition-colors border-b"
            style={{ color: 'rgba(245,240,232,0.4)', borderColor: 'rgba(139,105,20,0.3)' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.9)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.4)'; }}
          >
            Next: Bridge 02 →
          </Link>
        </div>
      </div>
    </section>
  );
}
